import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { rqKeys } from '@/react-query-keys'
import { cleanUpPhone, getMs, isCordovaApp } from '@/helpers'
import { readCookieValue } from '@/helpers/cookies'
import { apiRequest, ensureSidCookieForAuthFlow, setNewApiToken } from '@/helpers/request'
import { flushCordovaCookies } from '@/helpers/cordova-cookie-manager'
import { E_Colors } from '@/helpers/colors'
import { profile } from '@/profile'
import { routes } from '@/router/routes'
import { phoneMask } from '@/components/input/helpers'
import { Button, Flex, Input } from '@/components'
import { useAppStore } from '@/store'
import type { T_UniRes } from '@/types'
import { isOfflineAuthError, presentAuthServerMessage, showOfflineAuthMessage } from '../helpers'
import { toCanonicalRuMobile } from '../authorize-ticket'
import { useAuthStore } from '../../store'


declare const cordova: any
declare const SMSReceive: any

const readServerDetails = (value: unknown) => {
  if (!value || typeof value !== 'object') return undefined
  const details = (value as { details?: unknown }).details
  return typeof details === 'string' ? details : undefined
}

export const IncomingCallOrSms: FC = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const setToAuthorized = useAppStore((s) => s.setToAuthorized)
  const setCurrentStep = useAuthStore((s) => s.setCurrentStep)
  const userPhone = useAuthStore((s) => s.userPhone)
  const setUserPhone = useAuthStore((s) => s.setUserPhone)
  const authSecurity = useAuthStore((s) => s.authSecurity)

  const [ formState, setFormState ] = useState<
    'editPhone' | 'confirmByPhone' | 'confirmBySMS'
  >('confirmByPhone')

  const [ code, setCode ] = useState<string>('')

  const handleAuthMutationError = (error: unknown) => {
    if (isOfflineAuthError(error)) {
      showOfflineAuthMessage()
    }
  }

  const codeInputRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (formState === 'confirmBySMS') {
      const focusInput = () => {
        if (codeInputRef.current) {
          codeInputRef.current.focus()
        }
      }

      // Пытаемся установить фокус сразу и с задержкой для надежности
      focusInput()
      setTimeout(focusInput, 100)
      setTimeout(focusInput, 300)
    }
  }, [ formState ])

  // SMS Listener logic
  const smsHandlerRef = useRef<((e: any) => void) | null>(null)

  useEffect(() => {
    return () => {
      if (typeof SMSReceive !== 'undefined') {
        SMSReceive.stopWatch()
      }
      if (smsHandlerRef.current) {
        document.removeEventListener('onSMSArrive', smsHandlerRef.current)
      }
    }
  }, [])

  const startSmsListener = () => {
    if (typeof cordova === 'undefined' || typeof SMSReceive === 'undefined') return

    const permissions = cordova.plugins.permissions

    const handleSmsArrive = (e: any) => {
      const incomingSms = e.data

      if (incomingSms && incomingSms.body) {
        const codeMatch = incomingSms.body.match(/\b\d{4,6}\b/)

        if (codeMatch) {
          setCode(codeMatch[0])
          if (typeof SMSReceive !== 'undefined') SMSReceive.stopWatch()

          if (smsHandlerRef.current) {
            document.removeEventListener('onSMSArrive', smsHandlerRef.current)
            smsHandlerRef.current = null
          }
        }
      }
    }

    if (smsHandlerRef.current) {
      document.removeEventListener('onSMSArrive', smsHandlerRef.current)
    }

    smsHandlerRef.current = handleSmsArrive

    const startWatch = () => {
      SMSReceive.startWatch(
        () => document.addEventListener('onSMSArrive', handleSmsArrive),
        (err: any) => console.warn('SMS watch failed', err)
      )
    }

    const permissionList = [
      permissions.READ_SMS,
      permissions.RECEIVE_SMS,
    ]

    permissions.requestPermissions(
      permissionList,
      (status: any) => {
        if (status.hasPermission) startWatch()
      },
      (err: any) => console.warn('Permission request failed', err)
    )
  }

  // Запрос звонка
  const {
    mutate: incomingCallMutation,
    isPending: isRequestCallPending,
  } = useMutation({
    mutationFn: async () => {
      await ensureSidCookieForAuthFlow()
      const parentCookie = readCookieValue('parent')

      return await apiRequest<any>({
        method: 'client/visitor/authorize_create',
        param: {
          type: 'fcall',
          mobile: cleanUpPhone(userPhone),
          place: 'portal',
          form: profile.isRegistrationSpecific
            ? 'client/visitor/authorize_create_vsem'
            : 'client/visitor/authorize_create_site',
          ...(parentCookie ? { parent: parentCookie } : {}),
        },
      })
    },
    onError: handleAuthMutationError,
  })

  const [ waitForSmsAvailabilityTimeout, setWaitForSmsAvailabilityTimeout ] = useState<number>(0)
  const intervalIdRef = useRef<any>(null)

  const incomingCall = () => {
    setFormState('confirmByPhone')
    setCode('')

    incomingCallMutation()

    // Ждём некоторое время перед тем, как давть возможность отправить SMS

    if (intervalIdRef.current) clearInterval(intervalIdRef.current)
    setWaitForSmsAvailabilityTimeout(20)

    intervalIdRef.current = setInterval(() => {
      setWaitForSmsAvailabilityTimeout((prev) => {
        if (prev <= 1) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
          return 0
        }

        return prev - 1
      })
    }, getMs('1s'))
  }

  // Запускаем запрос входящего звонка при маунте
  useEffect(() => {
    // TEMP: отключено по задаче, чтобы при входе на экран
    // "Получить входящее sms" не отправлялся запрос fcall.
    // incomingCall()
  }, [])

  // Запрос SMS
  const {
    mutate: requestSmsMutation,
    isPending: isRequestSmsPending,
  } = useMutation({
    mutationFn: async () => {
      const mobile = toCanonicalRuMobile(userPhone)

      if (!authSecurity || !mobile) {
        message.error('Не удалось отправить SMS. Вернитесь к звонку и попробуйте снова')
        return null
      }

      await ensureSidCookieForAuthFlow()
      const parentCookie = readCookieValue('parent')

      return await apiRequest<any>({
        method: 'client/visitor/authorize_create',
        param: {
          type: 'sms',
          mobile,
          place: 'portal',
          form: profile.isRegistrationSpecific
            ? 'client/visitor/authorize_create_vsem'
            : 'client/visitor/authorize_create_site',
          security: authSecurity,
          ...(parentCookie ? { parent: parentCookie } : {}),
        },
      })
    },
    onSuccess: (res) => {
      if (!res) return
      if (res?.code !== 'ok') {
        message.error(
          presentAuthServerMessage(res?.message, res?.code, readServerDetails(res)) || 'Неизвестная ошибка',
        )
        return
      }

      setFormState('confirmBySMS')
      setCode('')

      startSmsListener()

      if (intervalIdRef.current) clearInterval(intervalIdRef.current)

      setWaitForSmsAvailabilityTimeout(20)

      intervalIdRef.current = setInterval(() => {
        setWaitForSmsAvailabilityTimeout((prev) => {
          if (prev <= 1) {
            clearInterval(intervalIdRef.current)
            intervalIdRef.current = null
            return 0
          }

          return prev - 1
        })
      }, getMs('1s'))
    },
    onError: handleAuthMutationError,
  })

  const requestSms = () => {
    requestSmsMutation()
  }

  // Подтверждение кодом

  const {
    mutate: confirmMutation,
    isPending: isConfirmPending,
  } = useMutation({
    mutationFn: async () => {
      await ensureSidCookieForAuthFlow()
      return await apiRequest<T_UniRes<{ id: number }>>({
        method: 'client/security/confirm',
        param: {
          type: 'action',
          value: code,
        },
      })
    },

    onSuccess: async (res) => {
      if (res?.code === 'ok') {
        setNewApiToken(res.token || null)

        const infoRetryDelaysMs = [ 0, getMs('1500ms'), getMs('3s'), getMs('5s'), getMs('8s') ]
        let isVisitorSessionReady = false

        for (const delayMs of infoRetryDelaysMs) {
          if (delayMs > 0) {
            await new Promise<void>((resolve) => {
              window.setTimeout(resolve, delayMs)
            })
          }

          try {
            const visitorInfoRes = await apiRequest<T_UniRes<any>>({
              method: 'client/visitor/info',
            })
            if (visitorInfoRes?.code === 'ok') {
              isVisitorSessionReady = true
              break
            }
          } catch (error) {
            if (isOfflineAuthError(error)) {
              showOfflineAuthMessage()
              return
            }
          }
        }

        if (!isVisitorSessionReady) {
          message.error('Не удалось подтвердить сессию. Пожалуйста, попробуйте снова')
          return
        }

        if (isCordovaApp) {
          await flushCordovaCookies()
        }

        setCurrentStep('initialScreen')
        setUserPhone(phoneMask)

        setToAuthorized()
        navigate(routes.home, { replace: true })

        await queryClient.invalidateQueries({ queryKey: [ rqKeys.userData ] })
        await queryClient.invalidateQueries({ queryKey: [ rqKeys.userBalance ] })
      } else {
        message.error(
          presentAuthServerMessage(res?.message, res?.code, readServerDetails(res)) || 'Неизвестная ошибка',
        )
      }
    },
    onError: handleAuthMutationError,
  })




  return (
    <Flex
      gap={20}
      justify='center'
      align='center'
      vertical
      style={{ width: '100%' }}
    >
      <Input
        label='Телефон'
        name='phone'
        $isPhone
        value={userPhone}
        disabled={formState !== 'editPhone'}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setUserPhone(e.currentTarget.value)
        }}
      />

      {formState !== 'editPhone' && <>
        {/* TEMP: скрыто по задаче для сценария "Получить входящее sms". */}
        {/*
          <div
            onClick={() => {
              if (intervalIdRef.current) clearInterval(intervalIdRef.current)
              setFormState('editPhone')
            }}
            style={{ color: E_Colors.EraBlue }}
          >
            Изменить номер
          </div>
        */}

        {formState === 'confirmBySMS' && (
          <Input
            key={formState}
            name='code'
            type='code'
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            $ref={codeInputRef}
            autoFocus
            label='Введите код из SMS'
          />
        )}

        <div
          onClick={requestSms}
          style={{
            color: (waitForSmsAvailabilityTimeout || isRequestSmsPending) ? E_Colors.GrayDD : E_Colors.EraBlue,
            pointerEvents: (waitForSmsAvailabilityTimeout || isRequestSmsPending) ? 'none' : 'all',
            cursor: (waitForSmsAvailabilityTimeout || isRequestSmsPending) ? 'not-allowed' : 'pointer',
          }}
        >
          Отправить SMS {
            waitForSmsAvailabilityTimeout
              ? `через ${waitForSmsAvailabilityTimeout}`
              : ''
          }
        </div>
      </>}

      <Button
        type='primary'
        onClick={() => {
          if (formState === 'editPhone') {
            incomingCall()
          } else {
            confirmMutation()
          }
        }}
        loading={isRequestCallPending || isConfirmPending}
        block
        disabled={
          isRequestCallPending
          || userPhone === phoneMask
          || (cleanUpPhone(userPhone).slice(2).length < 10)
          || isRequestSmsPending
          || isConfirmPending
          || formState !== 'confirmBySMS'
          || code.length === 0
        }
      >
        {
          formState === 'editPhone'
            ? 'Получить код'
            : 'Войти'
        }
      </Button>

      <Button
        onClick={() => setCurrentStep('outgoingCall')}
        block
      >
        Возврат к исходящему звонку
      </Button>
    </Flex>
  )
}

